import React, { useEffect, useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PauseCircle, Eye, Camera, Users, Home as HomeIcon, ClipboardList, UserCheck, UserX, Send, Clock } from "lucide-react";
import FunnelWidget from "@/components/FunnelWidget";

const STATUS_LABEL = {
  submitted: "Nieuw",
  awaiting_review: "In wacht",
  review_in_progress: "In behandeling",
  contact_seller: "Contact",
  appointment_scheduled: "Afspraak",
  photos_received: "Foto's ontvangen",
  approved: "Goedgekeurd",
  published: "Gepubliceerd",
  paused: "Gepauzeerd",
  offline: "Offline",
  sold: "Verkocht",
  rejected: "Afgewezen",
};

const STATUS_CLASS = {
  submitted: "wv-status-submitted",
  awaiting_review: "wv-status-review",
  review_in_progress: "wv-status-review",
  contact_seller: "wv-status-review",
  appointment_scheduled: "wv-status-review",
  photos_received: "wv-status-review",
  approved: "wv-status-approved",
  published: "wv-status-published",
  paused: "wv-status-offline",
  offline: "wv-status-offline",
  sold: "wv-status-sold",
  rejected: "wv-status-rejected",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [list, setList] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [pendingProps, setPendingProps] = useState([]);
  const [recentBuyers, setRecentBuyers] = useState([]);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [userAction, setUserAction] = useState(null); // {kind, user}
  const [actionMsg, setActionMsg] = useState("");
  const [actionSubj, setActionSubj] = useState("");

  const load = async () => {
    try {
      const [s, l, u, p, b] = await Promise.all([
        http.get("/admin/stats"),
        http.get("/admin/properties", { params: filter ? { status: filter } : {} }),
        http.get("/admin/users", { params: { status: "pending" } }),
        http.get("/admin/properties", { params: { status: "submitted" } }),
        http.get("/admin/buyers"),
      ]);
      setStats(s.data); setList(l.data); setPendingUsers(u.data);
      setPendingProps(p.data);
      // Nieuwe zoekprofielen van de afgelopen 7 dagen
      const weekAgo = Date.now() - 7 * 24 * 3600 * 1000;
      setRecentBuyers((b.data || []).filter(x => new Date(x.created_at).getTime() > weekAgo).slice(0, 10));
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try { await http.patch(`/admin/properties/${id}/status`, { status, admin_note: note || null }); toast.success(`Status: ${STATUS_LABEL[status]}`); setNote(""); setSelected(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const submitUserAction = async () => {
    if (!userAction) return;
    const { kind, user } = userAction;
    try {
      if (kind === "approve") {
        await http.patch(`/admin/users/${user.id}/approve`, { welcome_message: actionMsg || null });
        toast.success("Goedgekeurd — bevestigingsmail verzonden");
      } else if (kind === "reject") {
        await http.patch(`/admin/users/${user.id}/reject`, { welcome_message: actionMsg || null });
        toast.success("Afgewezen — mail verzonden");
      } else if (kind === "message") {
        if (!actionSubj || !actionMsg) { toast.error("Onderwerp en bericht zijn verplicht"); return; }
        await http.post(`/admin/users/${user.id}/message`, { subject: actionSubj, message: actionMsg });
        toast.success("Mail verzonden");
      }
      setUserAction(null); setActionSubj(""); setActionMsg(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">Admin</p>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-2">Woon Verder Operations</h1>
        </div>
        <a href="/admin/export" className="wv-btn-outline text-sm inline-flex items-center gap-2" data-testid="admin-export-link">Data Export & Clone</a>
        <a href="/admin/crm" className="wv-btn-primary text-sm inline-flex items-center gap-2" data-testid="admin-crm-link">CRM & Transacties</a>
      </div>

      {/* KPIs */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { k: "pending", label: "In behandeling", icon: ClipboardList },
          { k: "published", label: "Gepubliceerd", icon: HomeIcon },
          { k: "total", label: "Totaal woningen", icon: HomeIcon },
          { k: "photo_requests", label: "Fotografie", icon: Camera },
          { k: "buyers", label: "Zoekprofielen", icon: Users },
          { k: "users", label: "Gebruikers", icon: Users },
        ].map(({ k, label, icon: Ic }) => (
          <div key={k} className="p-4 rounded-2xl bg-white border border-slate-200" data-testid={`kpi-${k}`}>
            <Ic className="w-5 h-5 text-[color:var(--wv-green)]" />
            <p className="mt-2 text-xs uppercase font-bold tracking-wider text-slate-500">{label}</p>
            <p className="mt-1 font-mono-num text-2xl font-extrabold text-[color:var(--wv-obsidian)]">{stats?.[k] ?? "—"}</p>
          </div>
        ))}
      </div>

      {/* Nieuwe account-aanvragen (pending) */}
      {pendingUsers.length > 0 && (
        <div className="mt-8 p-5 rounded-2xl border-2 border-amber-300 bg-amber-50/60" data-testid="pending-users-section">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500 text-white grid place-items-center">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-amber-800">Actie vereist</p>
                <h2 className="font-display text-xl font-extrabold text-[color:var(--wv-obsidian)]">
                  {pendingUsers.length} nieuwe account-aanvra{pendingUsers.length === 1 ? "ag" : "gen"} in behandeling
                </h2>
                <p className="text-sm text-slate-600 mt-0.5">Keur goed of wijs af — gebruikers kunnen pas inloggen na uw goedkeuring.</p>
              </div>
            </div>
            <a href="/admin/crm" className="wv-btn-outline text-xs" data-testid="pending-users-view-all">Alle gebruikers →</a>
          </div>
          <div className="mt-4 grid gap-2">
            {pendingUsers.slice(0, 5).map((u) => (
              <div key={u.id} className="bg-white rounded-xl border border-amber-200 p-3 flex items-center gap-3 flex-wrap" data-testid={`pending-user-${u.id}`}>
                <div className="w-9 h-9 rounded-full bg-[color:var(--wv-navy)] text-white grid place-items-center text-xs font-bold flex-shrink-0">
                  {u.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{u.name} <span className="text-xs font-semibold text-slate-500 uppercase ml-1">{u.role}</span></p>
                  <p className="text-xs text-slate-500 truncate">{u.email} · {u.phone || "—"} · {u.country || "—"}</p>
                </div>
                <div className="flex items-center gap-1 flex-wrap">
                  <button onClick={() => { setUserAction({ kind: "approve", user: u }); setActionMsg(""); }}
                    className="wv-btn-green text-xs inline-flex items-center gap-1"
                    data-testid={`pending-approve-${u.id}`}><UserCheck className="w-3 h-3" /> Goedkeuren</button>
                  <button onClick={() => { setUserAction({ kind: "reject", user: u }); setActionMsg(""); }}
                    className="wv-btn-outline text-xs text-red-700 border-red-200 hover:bg-red-50 inline-flex items-center gap-1"
                    data-testid={`pending-reject-${u.id}`}><UserX className="w-3 h-3" /> Afwijzen</button>
                  <button onClick={() => { setUserAction({ kind: "message", user: u }); setActionSubj(""); setActionMsg(""); }}
                    className="wv-btn-outline text-xs inline-flex items-center gap-1"
                    data-testid={`pending-msg-${u.id}`}><Send className="w-3 h-3" /> Bericht</button>
                </div>
              </div>
            ))}
            {pendingUsers.length > 5 && (
              <p className="text-xs text-slate-500 mt-1 text-center">
                + {pendingUsers.length - 5} meer. <a href="/admin/crm" className="font-semibold text-[color:var(--wv-navy)] hover:underline">Bekijk alles →</a>
              </p>
            )}
          </div>
        </div>
      )}

      {/* Nieuwe woningaanmeldingen */}
      {pendingProps.length > 0 && (
        <div className="mt-6 p-5 rounded-2xl border-2 border-blue-300 bg-blue-50/60" data-testid="pending-props-section">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-600 text-white grid place-items-center">
                <HomeIcon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-blue-800">Nieuw ingediend</p>
                <h2 className="font-display text-xl font-extrabold text-[color:var(--wv-obsidian)]">
                  {pendingProps.length} nieuwe woningaanmelding{pendingProps.length === 1 ? "" : "en"} wacht op review
                </h2>
                <p className="text-sm text-slate-600 mt-0.5">Verkopers die hun woning hebben aangemeld — klik Review om goed te keuren.</p>
              </div>
            </div>
          </div>
          <div className="mt-4 grid gap-2">
            {pendingProps.slice(0, 5).map((p) => (
              <div key={p.id} className="bg-white rounded-xl border border-blue-200 p-3 flex items-center gap-3 flex-wrap" data-testid={`pending-prop-${p.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{p.title}</p>
                  <p className="text-xs text-slate-500 truncate">{p.city} · € {Number(p.price).toLocaleString("nl-NL")} · {p.seller_email}</p>
                </div>
                <button onClick={() => setSelected(p)} className="wv-btn-primary text-xs inline-flex items-center gap-1" data-testid={`pending-prop-review-${p.id}`}>
                  <Eye className="w-3 h-3" /> Review
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Nieuwe zoekprofielen van afgelopen 7 dagen */}
      {recentBuyers.length > 0 && (
        <div className="mt-6 p-5 rounded-2xl border-2 border-emerald-300 bg-emerald-50/60" data-testid="recent-buyers-section">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-600 text-white grid place-items-center">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-800">Nieuwe zoekprofielen</p>
                <h2 className="font-display text-xl font-extrabold text-[color:var(--wv-obsidian)]">
                  {recentBuyers.length} nieuw zoekprofiel{recentBuyers.length === 1 ? "" : "en"} deze week
                </h2>
                <p className="text-sm text-slate-600 mt-0.5">Kopers die een gratis zoekprofiel hebben gemaakt — zie welke woningen matchen.</p>
              </div>
            </div>
            <a href="/admin/crm" className="wv-btn-outline text-xs" data-testid="recent-buyers-view-all">Alle zoekprofielen →</a>
          </div>
          <div className="mt-4 grid gap-2">
            {recentBuyers.slice(0, 5).map((b) => (
              <div key={b.id || b.email} className="bg-white rounded-xl border border-emerald-200 p-3 flex items-center gap-3 flex-wrap" data-testid={`recent-buyer-${b.id || b.email}`}>
                <div className="w-9 h-9 rounded-full bg-emerald-600 text-white grid place-items-center text-xs font-bold flex-shrink-0">
                  {b.name?.charAt(0)?.toUpperCase() || "?"}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{b.name} <span className="text-xs text-slate-500 font-normal">· {b.email}</span></p>
                  <p className="text-xs text-slate-500 truncate">
                    Budget: {b.budget_min ? `€${Number(b.budget_min).toLocaleString("nl-NL")}` : "—"}–{b.budget_max ? `€${Number(b.budget_max).toLocaleString("nl-NL")}` : "—"}
                    {b.locations?.length ? ` · ${b.locations.join(", ")}` : ""}
                    {b.min_bedrooms ? ` · ${b.min_bedrooms}+ slk` : ""}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="mt-8 flex flex-wrap gap-2">
        <button onClick={() => setFilter("")} className={`wv-chip ${filter === "" ? "active" : ""}`} data-testid="filter-all">Alles</button>
        {["submitted", "awaiting_review", "review_in_progress", "published", "offline", "rejected", "sold"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`wv-chip ${filter === s ? "active" : ""}`} data-testid={`filter-${s}`}>{STATUS_LABEL[s]}</button>
        ))}
      </div>

      {/* Table */}
      <div className="mt-4 bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left">
            <tr>
              <th className="p-3 font-semibold">Titel</th>
              <th className="p-3 font-semibold hidden md:table-cell">Plaats</th>
              <th className="p-3 font-semibold">Prijs</th>
              <th className="p-3 font-semibold hidden md:table-cell">Verkoper</th>
              <th className="p-3 font-semibold">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`admin-row-${p.id}`}>
                <td className="p-3">
                  <p className="font-semibold text-slate-800">{p.title}</p>
                  <p className="text-xs text-slate-500 md:hidden">{p.city}</p>
                </td>
                <td className="p-3 hidden md:table-cell">{p.city}</td>
                <td className="p-3 font-mono-num">€ {Number(p.price).toLocaleString("nl-NL")}</td>
                <td className="p-3 hidden md:table-cell text-xs">{p.seller_email}</td>
                <td className="p-3"><span className={`wv-status-pill ${STATUS_CLASS[p.status]}`}>{STATUS_LABEL[p.status] || p.status}</span></td>
                <td className="p-3 text-right">
                  <button onClick={() => setSelected(p)} className="wv-btn-outline text-xs" data-testid={`admin-review-${p.id}`}>
                    <Eye className="w-3.5 h-3.5 inline mr-1" /> Review
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-slate-500">Geen woningen in deze status.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setSelected(null)}>
          <div className="max-w-3xl w-full max-h-[90vh] overflow-y-auto bg-white rounded-2xl" onClick={(e) => e.stopPropagation()} data-testid="admin-review-modal">
            <div className="p-6 border-b border-slate-100 flex items-start justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-widest font-bold text-slate-500">{selected.property_type}</p>
                <h2 className="font-display text-2xl font-extrabold">{selected.title}</h2>
                <p className="text-sm text-slate-600 mt-1">{selected.address}, {selected.postal_code} {selected.city}</p>
              </div>
              <span className={`wv-status-pill ${STATUS_CLASS[selected.status]}`}>{STATUS_LABEL[selected.status]}</span>
            </div>
            <div className="p-6 space-y-4">
              <FunnelWidget propertyId={selected.id} scope="admin" />
              {selected.photos?.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {selected.photos.slice(0, 6).map((ph, i) => (
                    <img key={i} src={ph.data_url} alt="" className="aspect-square object-cover rounded-xl border border-slate-200" />
                  ))}
                </div>
              )}
              <dl className="grid sm:grid-cols-2 gap-3 text-sm">
                <div><dt className="text-slate-500">Vraagprijs</dt><dd className="font-mono-num font-bold">€ {Number(selected.price).toLocaleString("nl-NL")}</dd></div>
                <div><dt className="text-slate-500">Oppervlak / kamers</dt><dd className="font-mono-num font-bold">{selected.area_m2} m² · {selected.rooms} kamers · {selected.bedrooms} slk</dd></div>
                <div><dt className="text-slate-500">Verkoper</dt><dd>{selected.seller_name} — {selected.seller_email}</dd></div>
                <div><dt className="text-slate-500">Fotografie service</dt><dd>{selected.photography_service_requested ? "Aangevraagd" : "Nee"}</dd></div>
              </dl>
              {selected.description && <p className="text-sm text-slate-700 whitespace-pre-line">{selected.description}</p>}
              <div>
                <label className="text-sm font-semibold">Admin notitie (optioneel)</label>
                <textarea className="wv-input mt-1" rows="2" value={note} onChange={(e) => setNote(e.target.value)} data-testid="admin-note-input" />
              </div>
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                <button onClick={() => setStatus(selected.id, "review_in_progress")} className="wv-btn-outline text-sm" data-testid="admin-btn-review">Review starten</button>
                <button onClick={() => setStatus(selected.id, "contact_seller")} className="wv-btn-outline text-sm" data-testid="admin-btn-contact">Contact opnemen</button>
                <button onClick={() => setStatus(selected.id, "appointment_scheduled")} className="wv-btn-outline text-sm" data-testid="admin-btn-appointment">Afspraak</button>
                <button onClick={() => setStatus(selected.id, "approved")} className="wv-btn-outline text-sm" data-testid="admin-btn-approve">Goedkeuren</button>
                <button onClick={() => setStatus(selected.id, "published")} className="wv-btn-green text-sm inline-flex items-center gap-1" data-testid="admin-btn-publish"><CheckCircle2 className="w-4 h-4" /> Publiceren</button>
                <button onClick={() => setStatus(selected.id, "paused")} className="wv-btn-outline text-sm inline-flex items-center gap-1" data-testid="admin-btn-pause"><PauseCircle className="w-4 h-4" /> Pauzeren</button>
                <button onClick={() => setStatus(selected.id, "offline")} className="wv-btn-outline text-sm" data-testid="admin-btn-offline">Offline</button>
                <button onClick={() => setStatus(selected.id, "rejected")} className="wv-btn-outline text-sm text-red-700 border-red-200 hover:bg-red-50 inline-flex items-center gap-1" data-testid="admin-btn-reject"><XCircle className="w-4 h-4" /> Afwijzen</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* User approval modal */}
      {userAction && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setUserAction(null)}>
          <div className="max-w-lg w-full bg-white rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="dashboard-user-modal">
            <p className="text-xs uppercase tracking-widest font-bold text-slate-500">
              {userAction.kind === "approve" && "Gebruiker goedkeuren"}
              {userAction.kind === "reject" && "Gebruiker afwijzen"}
              {userAction.kind === "message" && "Bericht sturen"}
            </p>
            <h2 className="font-display text-2xl font-extrabold mt-1">{userAction.user.name}</h2>
            <p className="text-sm text-slate-600 mt-1">{userAction.user.email}</p>
            {userAction.user.phone && <p className="text-xs text-slate-500">{userAction.user.phone} · {userAction.user.country || "—"}</p>}

            {userAction.kind === "approve" && (
              <>
                <p className="mt-4 text-sm text-slate-700">Er wordt automatisch een bevestigingsmail gestuurd. Optioneel persoonlijk welkomstbericht:</p>
                <textarea className="wv-input mt-3" rows="4" placeholder="Optioneel — persoonlijk welkomstbericht"
                  value={actionMsg} onChange={(e) => setActionMsg(e.target.value)} data-testid="dashboard-user-welcome" />
              </>
            )}
            {userAction.kind === "reject" && (
              <>
                <p className="mt-4 text-sm text-slate-700">De gebruiker ontvangt een afwijzingsmail. Optioneel reden toevoegen:</p>
                <textarea className="wv-input mt-3" rows="4" placeholder="Optioneel — reden"
                  value={actionMsg} onChange={(e) => setActionMsg(e.target.value)} data-testid="dashboard-user-reject-reason" />
              </>
            )}
            {userAction.kind === "message" && (
              <>
                <label className="text-sm font-semibold mt-4 block">Onderwerp</label>
                <input className="wv-input mt-1" value={actionSubj} onChange={(e) => setActionSubj(e.target.value)} data-testid="dashboard-user-msg-subject" />
                <label className="text-sm font-semibold mt-3 block">Bericht</label>
                <textarea className="wv-input mt-1" rows="6" value={actionMsg} onChange={(e) => setActionMsg(e.target.value)} data-testid="dashboard-user-msg-body" />
              </>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setUserAction(null)} className="wv-btn-outline text-sm" data-testid="dashboard-user-cancel">Annuleren</button>
              <button onClick={submitUserAction}
                className={`text-sm ${userAction.kind === "reject" ? "wv-btn-primary bg-red-600 hover:bg-red-700 border-red-600" : "wv-btn-green"}`}
                data-testid="dashboard-user-confirm">
                {userAction.kind === "approve" && "Goedkeuren & mail verzenden"}
                {userAction.kind === "reject" && "Afwijzen & mail verzenden"}
                {userAction.kind === "message" && "Verzenden"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
